<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="visible"
      :size="'md'"
      test-id="update-msp-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'">{{ t("edit-msp") }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>
        <!-- edit msp -->
        <msp-step-index
          v-if="mspStep.current"
          v-model:form="mspForm"
          :is-available-to-update-country="isAvailableToUpdateCountry"
          :organisation-object="orginisationObject"
          :is-system-account="isSystemAccount"
          @form-validation="isMspsFormValid = $event"
        />
      </div>
      <!-- edit Solutions step 2-->
      <solutionsStepIndex
        v-if="solutionsStep.current"
        v-model:form="solutionsForm"
        :customer-name="mspForm.name"
        :list-all-licences="listAllLicences"
        v-model:isCreateMspButtonDisabled="isCreateMspButtonDisabled"
        v-model:showTooltip="showTooltip"
      />
      <!-- footer -->
      <template #[`footer.decline`]>
        <cl-button
          data-test-id="update-msp-btn-cancel"
          :variant="'secondary-ghost'"
          @on-click="onClose"
        >
          <span>{{ t("cancel") }}</span>
        </cl-button>
        <template v-if="solutionsStep.current">
          <cl-button
            data-test-id="edit-msp-input-btn-go-back-to-admin-step"
            :variant="'secondary-outline'"
            @on-click="goToStep(1)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="mspStep.current">
          <cl-button
            variant="secondary"
            data-test-id="edit-msp-btn-go-to-customers-step"
            :disabled="isMspsFormValid"
            @on-click="
              () => {
                mspStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <cl-button v-if="updatingMsp" :variant="'secondary'" disabled>
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>Saving...</span>
          </cl-button>
          <cl-button
            v-else
            v-tippy="tooltip"
            variant="secondary"
            data-test-id="create-save-msp-btn-save-save"
            :disabled="isCreateMspButtonDisabled"
            @on-click="updateMsp"
          >
            <span>{{ t("edit-msp") }}</span>
          </cl-button>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";
import type { Step } from "@cybersentriq/frontend-component-library/dist/types/src/components/stepper/types";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useArctitanStore } from "@/store/arctitan.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { TIER } from "@/constants";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
  SEG_SERVICE,
} from "@/utils/services";

import { useI18n } from "vue-i18n";

import { LicenceWithSeats } from "@/types/licenceSolutions.ts";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { TUpdateMspsProps } from "./t-update-msps.types";
import { useIsSEGCheckboxAvailable } from "@/composables/useFlags/useIsSEGCheckboxAvailable";
import mspStepIndex from "./msp-step/msp-step.index.vue";
import solutionsStepIndex from "./solutions-step/solutions-step.index.vue";

const { isSEGCheckboxAvailable } = useIsSEGCheckboxAvailable();

const toastStore = useToastStore();

const props = defineProps<TUpdateMspsProps>();
const { visible, orginisationObject, listAllLicences } = toRefs(props);

const { t } = useI18n();
const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();
const arctitanStore = useArctitanStore();
const isMspsFormValid = ref<boolean>(false);
const isSystemAccount = computed(() => {
  const typeName = authStore.selectedAccount?.type_name;
  const orgType = orginisationObject.value.organisation_type;
  return typeName === TIER.system && orgType === TIER.system;
});

const isAvailableToUpdateCountry = computed<boolean>(() => {
  return (
    orginisationObject.value.country === "" ||
    orginisationObject.value.country === "-1"
  );
});
const getSolutionDetails = (solution_uuid: string) => {
  return listAllLicences.value.licences.find(
    (licence) => licence.solution_uuid === solution_uuid,
  );
};

const isServiceAvailable = (service_uuid: string) => {
  const service = listAllLicences.value?.licences?.find(
    (obj: LicenceWithSeats) => obj.solution_uuid === service_uuid,
  );
  return service?.seats_purchased ?? 0;
};
const hasServiceAvailbe = (service_uuid: string) => {
  return listAllLicences.value.licences.some(
    (obj: LicenceWithSeats) => obj.solution_uuid === service_uuid || false,
  );
};

const mspForm = ref({
  name: "",
  description: "",
  country: "",
  customerID: 0,
  uuid: "",
});
const solutionsForm = ref({
  emailSecurity: {
    seats_purchased: isServiceAvailable(PHISH_SERVICE) || "",
    expires_at: getSolutionDetails(PHISH_SERVICE)?.expires_at,
    maxioSubID: "",
    switcher: hasServiceAvailbe(PHISH_SERVICE),
  },
  emailArchiving: {
    seats_purchased: isServiceAvailable(ARC_SERVICE) || "",
    expires_at: getSolutionDetails(ARC_SERVICE)?.expires_at,
    maxioSubID: "",
    switcher: hasServiceAvailbe(ARC_SERVICE),
  },
  phishingSimulation: {
    seats_purchased: isServiceAvailable(PHISHING_SIMULATION) || "",
    expires_at: getSolutionDetails(PHISHING_SIMULATION)?.expires_at,
    maxioSubID: "",
    switcher: hasServiceAvailbe(PHISHING_SIMULATION),
  },
  sat: {
    seats_purchased: isServiceAvailable(SAT_SERVICE) || "",
    expires_at: getSolutionDetails(SAT_SERVICE)?.expires_at,
    maxioSubID: "",
    switcher: hasServiceAvailbe(SAT_SERVICE),
  },
  seg: {
    expires_at: getSolutionDetails(SEG_SERVICE)?.expires_at,
    maxioSubID: "",
    switcher: hasServiceAvailbe(SEG_SERVICE),
  },
});

const showTooltip = ref<boolean>(false);
watch(showTooltip, (newVal) => {
  tooltip.value.content = newVal ? t("select-solution-to-save-changes") : "";
});
const tooltip = ref({
  content: "",
  placement: "top",
});
const mspStep = ref<Step>({
  index: 1,
  label: t("edit-msp"),
  current: true,
  completed: false,
  actionable: false,
});

const solutionsStep = ref<Step>({
  index: 2,
  label: t("step-edit-solutions"),
  current: false,
  completed: false,
  actionable: false,
});

const steps = computed<Step[]>(() => [mspStep.value, solutionsStep.value]);
const stepsRefs = [mspStep, solutionsStep];
const isCreateMspButtonDisabled = ref<boolean>(false);

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;

      continue;
    }

    step.value.current = false;
  }
};

const updatingMsp = ref(false);

const updateMsp = async () => {
  try {
    updatingMsp.value = true;
    await updateOrginisation();

    if (isSEGCheckboxAvailable.value) {
      if (solutionsForm.value.seg.switcher && hasServiceAvailbe(SEG_SERVICE)) {
        await updateSEGLicenses();
      } else if (hasServiceAvailbe(SEG_SERVICE)) {
        await removeLicenses(SEG_SERVICE);
      } else if (
        solutionsForm.value.seg.switcher &&
        !hasServiceAvailbe(SEG_SERVICE)
      ) {
        await createSEGLicenses();
      }
    }

    if (
      solutionsForm.value.emailSecurity.switcher &&
      hasServiceAvailbe(PHISH_SERVICE)
    ) {
      await updateEmailSecurityLicenses();
    } else if (hasServiceAvailbe(PHISH_SERVICE)) {
      await removeLicenses(PHISH_SERVICE);
    } else if (
      solutionsForm.value.emailSecurity.switcher &&
      !hasServiceAvailbe(PHISH_SERVICE)
    ) {
      await createEmailSecurityLicenses();
    }

    if (
      solutionsForm.value.phishingSimulation.switcher &&
      hasServiceAvailbe(PHISHING_SIMULATION)
    ) {
      await updatePhishingSimLicenses();
    } else if (hasServiceAvailbe(PHISHING_SIMULATION)) {
      await removeLicenses(PHISHING_SIMULATION);
    } else if (
      solutionsForm.value.phishingSimulation.switcher &&
      !hasServiceAvailbe(PHISHING_SIMULATION)
    ) {
      await createPhishingSimLicenses();
    }

    if (solutionsForm.value.sat.switcher && hasServiceAvailbe(SAT_SERVICE)) {
      await updateSATLicenses();
    } else if (hasServiceAvailbe(SAT_SERVICE)) {
      await removeLicenses(SAT_SERVICE);
    } else if (
      solutionsForm.value.sat.switcher &&
      !hasServiceAvailbe(SAT_SERVICE)
    ) {
      await createSATLicenses();
    }

    if (
      solutionsForm.value.emailArchiving.switcher &&
      hasServiceAvailbe(ARC_SERVICE)
    ) {
      try {
        await arctitanStore.hasTagMsp({
          formData: mspForm.value,
          uuid: orginisationObject.value.uuid,
        });
      } catch (e) {
        /* eslint-disable no-console */
        console.log(e);
      } finally {
        await updateEmailArchivingLicenses();
      }
    } else if (hasServiceAvailbe(ARC_SERVICE)) {
      await removeLicenses(ARC_SERVICE);
    } else if (
      solutionsForm.value.emailArchiving.switcher &&
      !hasServiceAvailbe(ARC_SERVICE)
    ) {
      try {
        await arctitanStore.hasTagMsp({
          formData: mspForm.value,
          uuid: orginisationObject.value.uuid,
        });
        if (mspForm.value.country) {
          await arctitanStore.createMspArctian({
            formData: {
              name: mspForm.value.name,
              country: mspForm.value.country,
            },
            uuid: mspForm.value.uuid,
          });
        }
      } catch (e) {
        /* eslint-disable no-console */
        console.log(e);
      } finally {
        await createEmailArchivingLicenses();
      }
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
    updatingMsp.value = false;
  } finally {
    finished();
  }
};

const finished = async () => {
  updatingMsp.value = false;
  emit("on-update");

  toastStore.displayToast({
    title: t("success"),
    message: t("msp-with-name-updated-successfully", {
      CustomerName: mspForm.value.name,
    }),
    duration: 5000,
    variant: "success",
    testId: "update-msp-toast-success",
  });
};
const updateOrginisation = async () => {
  try {
    await organisationsStore.updateOrg({
      formData: mspForm.value,
      parent_uuid: null,
      organisation_type: TIER.msps,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const createEmailSecurityLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISH_SERVICE,
      formData: solutionsForm.value.emailSecurity,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSEGLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SEG_SERVICE,
      formData: {
        ...solutionsForm.value.seg,
        seats_purchased: solutionsForm.value.emailSecurity.seats_purchased,
      },
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.msps,
      // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.com/cybersentriq/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
      shouldCallV1: false,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createPhishingSimLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISHING_SIMULATION,
      formData: solutionsForm.value.phishingSimulation,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSATLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SAT_SERVICE,
      formData: solutionsForm.value.sat,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const updateEmailSecurityLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(PHISH_SERVICE)?.uuid,
      formData: solutionsForm.value.emailSecurity,
      uuid: orginisationObject.value.uuid,
      solution_uuid: PHISH_SERVICE,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const removeLicenses = async (solution_uuid: string) => {
  try {
    await licenceStore.removeLicence({
      licence_uuid: getSolutionDetails(solution_uuid)?.uuid,
      uuid: orginisationObject.value.uuid,
      solution_uuid: solution_uuid,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const updateSEGLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(SEG_SERVICE)?.uuid,
      formData: {
        ...solutionsForm.value.seg,
        seats_purchased: solutionsForm.value.emailSecurity.seats_purchased,
      },
      uuid: orginisationObject.value.uuid,
      solution_uuid: SEG_SERVICE,
      // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.com/cybersentriq/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
      shouldCallV1: false,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const updatePhishingSimLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(PHISHING_SIMULATION)?.uuid,
      formData: solutionsForm.value.phishingSimulation,
      uuid: orginisationObject.value.uuid,
      solution_uuid: PHISHING_SIMULATION,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const updateSATLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(SAT_SERVICE)?.uuid,
      formData: solutionsForm.value.sat,
      uuid: orginisationObject.value.uuid,
      solution_uuid: SAT_SERVICE,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const updateEmailArchivingLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(ARC_SERVICE)?.uuid,
      formData: solutionsForm.value.emailArchiving,
      uuid: orginisationObject.value.uuid,
      solution_uuid: ARC_SERVICE,
    });
  } catch {
    updatingMsp.value = false;
  }
};
const createEmailArchivingLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: ARC_SERVICE,
      formData: solutionsForm.value.emailArchiving,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};

const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-update"): void;
}>();
const onClose = () => {
  emit("on-close");
};

watch(
  () => orginisationObject.value,
  (organisation) => {
    if (organisation) {
      mspForm.value.name = organisation.name || "";
      mspForm.value.description = organisation.description || "";
      mspForm.value.country = organisation.country || "";
      mspForm.value.customerID = organisation.billing_system_id || 0;
      mspForm.value.uuid = organisation.uuid || "";
    }
  },
  { immediate: true },
);
</script>
